import { useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import HealthScoreCommentary from '@/components/Insights/HealthScoreCommentary';
import { computeSharepointCollabAggregate, weighted, pct as pctUtil } from '../../../utils/scoring';
import * as Sentry from '@sentry/react';

/* OverallHealthScore (refactored scoring with comprehensive security and improved metrics)
   - Enhanced security scoring with Threat Hunting, Network Security, and GitHub Security data
   - Improved asset management with encryption and OS compliance
   - More realistic weighting reflecting actual IT department responsibilities
   - Better handling of high-volume metrics with logarithmic scaling
*/

const pct = pctUtil; // local alias
const clamp = (v,min,max)=> Math.min(max,Math.max(min,v));

// Updated weights to better reflect IT department responsibilities for KPI scoring
// Total = 100%: Security is critical, Infrastructure/Reliability, Support, etc.
// Health score category weights - must sum to exactly 1.0 (100%)
// Rebalanced to give EVERY IT Report section a fair, distinct contribution — including
// Automation & Self-Healing, Microsoft 365 configuration posture, and the Router Fleet —
// so the headline score reflects the full breadth of the department's responsibilities.
const WEIGHTS = {
  asset: 0.06,         // Device compliance, encryption, OS currency
  security: 0.11,      // Defender, Threat Hunting, Alert Hygiene
  repoSecurity: 0.05,  // GitHub security (CodeQL, Dependabot, Secrets)
  compliance: 0.08,    // MS Purview (DLP, sensitivity labels, retention policies)
  m365compliance: 0.03,// Microsoft 365 configuration posture / gaps (Purview+Intune+Defender+Endpoint)
  backup: 0.08,        // Rubrik backup/restore
  support: 0.08,       // IT Related Action Items
  automation: 0.05,    // Automation & Self-Healing (monitoring coverage + auto-remediation)
  infra: 0.08,         // Proxmox virtualization
  network: 0.07,       // Internet uptime + network security
  routerFleet: 0.03,   // MikroTik router fleet management (online %, provisioning, firmware)
  dev: 0.07,           // DORA metrics
  projectMgmt: 0.04,   // GitHub Issues Health (velocity, bug responsiveness)
  boardFlow: 0.03,     // GitHub Project Board flow (WIP staleness, deployment throughput)
  sentry: 0.04,        // Sentry production error monitoring
  email: 0.04,         // Exchange
  sharepoint: 0.04,    // SharePoint
  telephony: 0.01,     // Porta call reporting
  tenant: 0.01         // Tenant statements automation
}; // Total: 1.00 (100%)

// Map change failure rate (0-1) to score (1 best -> 0) inverted.
const cfrScore = (cfr) => cfr == null ? null : pct((1 - cfr) * 100);
// Lead time (ISO or minutes) to score: 0-24h => 100, 7d => 0 linear.
const leadTimeScore = (mins) => { if (!Number.isFinite(mins)) return null; const max=7*24*60,best=24*60; if (mins<=best) return 100; if (mins>=max) return 0; return pct(100 * (1 - (mins-best)/(max-best))); };
// MTTR to score: 0-60m => 100, 24h => 80, 7d => 0 (piecewise/linear between thresholds for smoother drop)
const mttrScoreFn = (mins) => { if(!Number.isFinite(mins)) return null; const best=60; const mid=24*60; const worst=7*24*60; if (mins<=best) return 100; if (mins<=mid) { return 80 + ( (mid - mins)/(mid-best) ) * 20; } if (mins>=worst) return 0; return ( (worst - mins)/(worst - mid) ) * 80; };
// Improved duration parser (ISO8601 P.. and HH:MM:SS or DD.HH:MM:SS) matching GithubActivitySummary
const parseDurationMinutes = (v) => {
  if (v == null) return null;
  if (typeof v === 'number') return v / 60000; // assume ms
  const s = String(v);
  if (/^P/i.test(s)) { // ISO8601 period e.g. PT2H30M, P1DT5H
    const d = parseFloat(/(?:(\d+)D)/i.exec(s)?.[1] || '0');
    const h = parseFloat(/T(?:(\d+)H)/i.exec(s)?.[1] || '0');
    const m = parseFloat(/(?:(\d+)M)/i.exec(s)?.[1] || '0');
    return d*24*60 + h*60 + m;
  }
  // Match optional day prefix (as days.) then HH:MM(:SS)
  const m2 = s.match(/^(?:(\d+)\.)?(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (m2) {
    const days = Number(m2[1]||0);
    const hours = Number(m2[2]||0);
    const minutes = Number(m2[3]||0);
    // seconds (ignored for now): Number(m2[4]||0)
    return days*24*60 + hours*60 + minutes;
  }
  // Simple number with trailing 'm' (minutes) or plain number treat as minutes
  if (/^\d+m?$/.test(s)) return parseInt(s,10);
  return null;
};
const scoreToAngle = (score) => { const s=clamp(score,0,100); return -110 + (s/100)*220; };

// NEW: Tiered deployment frequency mapping (deployments / week) aligned loosely to DORA maturity bands
function mapDeployFreqScore(freq){
  if (freq == null) return null;
  if (freq >= 14) return 100;          // many per day
  if (freq >= 7)  return 95;           // daily
  if (freq >= 3.5) return 90;          // every couple of days
  if (freq >= 1)  return 80;           // weekly
  if (freq >= 0.25) return 60;         // monthly
  if (freq >= 0.083) return 40;        // quarterly
  if (freq > 0) return 25;             // rarer
  return 0;
}

export default function OverallHealthScore(){
  const { apiFetch } = useAuth();
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState('');
  const [data,setData]=useState({});

  const ghOwner = import.meta.env.VITE_GITHUB_OWNER || 'Capital-Land-Management-Services';
  const ghRepo = import.meta.env.VITE_GITHUB_REPO || 'CapTrac-ReactCore';

  const now = new Date();
  const prevMonthDate = new Date(now.getFullYear(), now.getMonth()-1, 1);
  const monthKey = `OHS-${prevMonthDate.getFullYear()}-${String(prevMonthDate.getMonth()+1).padStart(2,'0')}`;
  const priorMonthKey = (()=>{ const prior = new Date(prevMonthDate.getFullYear(), prevMonthDate.getMonth()-1,1); return `OHS-${prior.getFullYear()}-${String(prior.getMonth()+1).padStart(2,'0')}`; })();

  useEffect(()=>{ let ignore=false; (async()=>{
    setLoading(true); setError('');
    try {
      const startPrev = prevMonthDate.toISOString().substring(0,10);
      const endPrev = new Date(prevMonthDate.getFullYear(), prevMonthDate.getMonth()+1, 0).toISOString().substring(0,10);
      const requests = [
        apiFetch('/api/intune/device-inventory-summary'),
        apiFetch('/api/defender/stats'),
        apiFetch('/api/rubrik/backups-summary'),
        apiFetch('/api/proxmox/summary?period=previousMonth'),
        apiFetch(`/api/RouterConfig/mall-outage-events?startDate=${startPrev}&endDate=${endPrev}`),
        apiFetch(`/api/github/repo-summary/compare/cache?owner=${encodeURIComponent(ghOwner)}&repo=${encodeURIComponent(ghRepo)}`),
        apiFetch('/api/exchange/email-analytics/compare'),
        apiFetch('/api/itactionitems/summary'),
        apiFetch('/api/sharepoint/health-metrics'),
        apiFetch('/api/sharepoint/sites-usage/summary'),
        apiFetch('/api/sharepoint/sites-usage/activity?period=D90&inactiveThresholdDays=90'),
        apiFetch('/api/porta/call-daily?months=1'),
        apiFetch('/api/porta/call-tops'),
        apiFetch('/api/tenant-statements/monthly-summary?months=2'),
        // NEW: Additional security data for comprehensive scoring
        apiFetch('/api/ThreatHunting/summary?daysBack=30'),
        apiFetch(`/api/NetworkSecurity/security/aggregated?startDate=${startPrev}&endDate=${endPrev}`),
        apiFetch(`/api/Github/security-summary?owner=${encodeURIComponent(ghOwner)}&repo=${encodeURIComponent(ghRepo)}`),
        // NEW: Asset Management and Compliance
        apiFetch('/api/mspurview/summary'),
        // NEW: GitHub Issues Health + Sentry Production Health + Project Board Flow
        apiFetch(`/api/Github/issues-health?owner=${encodeURIComponent(ghOwner)}&repo=${encodeURIComponent(ghRepo)}`),
        apiFetch('/api/Sentry/production-health'),
        apiFetch(`/api/Github/project-flow?orgName=${encodeURIComponent(ghOwner)}&projectNumber=28`),
        // NEW: Automation & Self-Healing, Router Fleet, and Microsoft 365 configuration posture
        // so every IT Report section contributes to the overall health score.
        apiFetch('/api/SystemHealth/automation-summary?days=30'),
        apiFetch('/api/RouterConfig/fleet-summary?days=30'),
        apiFetch('/api/EndpointHealth/alerts/statistics'),
      ];
      const settled = await Promise.allSettled(requests);
      if (ignore) return;
      // Safely parse JSON — returns null if response is missing, non-ok, or has
      // an empty/truncated body (e.g. proxy 504 that arrives as status 200 with no content).
      const toJson = async (r) => {
        if (!r || !r.ok) return null;
        try { return await r.json(); }
        catch { return null; }
      };
      const [intune, defender, rubrik, proxmox, mall, gh, mail, tickets, spHealth, spSummary, spActivity, portaDaily, portaTops, tenantStatements, threatHunting, networkSecurity, githubSecurity, msPurview, githubIssuesHealth, sentryHealth, projectFlow, automation, routerFleet, endpointStats] = await Promise.all(settled.map(s => s.status==='fulfilled'? toJson(s.value): null));
      setData({ intune, defender, rubrik, proxmox, mall, githubCompare: gh, mailCompare: mail, tickets, sharepointHealth: spHealth, sharepointSummary: spSummary, sharepointActivity: spActivity, portaDaily, portaTops, tenantStatements, threatHunting, networkSecurity, githubSecurity, msPurview, githubIssuesHealth, sentryHealth, projectFlow, automation, routerFleet, endpointStats });
    }
    catch (e) {
        Sentry.captureException(e);
        if(!ignore) setError(e.message);
    }
    finally { if(!ignore) setLoading(false); }
  })(); return ()=>{ ignore=true; }; }, [apiFetch, monthKey, ghOwner, ghRepo]);

  const categoryScores = useMemo(()=>{
    const cats = [];

    // Normalize GitHub compare payload shape (supports {previous,prior} or {data:{previous,prior}})
    const ghCompare = (data.githubCompare && data.githubCompare.data) ? data.githubCompare.data : data.githubCompare;

    // Device Management (Intune - enhanced with compliance states breakdown)
    const complianceStates = data.intune?.complianceStates || data.intune?.ComplianceStates || {};
    const totalDevices = data.intune?.totalDevices || data.intune?.TotalDevices || 0;
    const compliantCount = complianceStates.Compliant || complianceStates.compliant || 0;
    const deviceCompliance = totalDevices > 0 ? pct((compliantCount / totalDevices) * 100) : pct(data.intune?.previousMonthComplianceRate);
    
    // Use previousMonthComplianceRate if available (more accurate for last month reporting)
    const complianceRate = data.intune?.previousMonthComplianceRate ?? data.intune?.PreviousMonthComplianceRate ?? deviceCompliance;
    
    cats.push({ 
      key:'asset', 
      name:'Device Management', 
      ...weighted([{ value: pct(complianceRate), weight:1 }]), 
      items:[{ label:'Device Compliance', value: pct(complianceRate) }],
      notes: totalDevices > 0 ? `${compliantCount}/${totalDevices} compliant` : null
    });

    // Security Monitoring (enhanced with Threat Hunting data)
    // Defender secure score
    let secureScorePrev=null; 
    if (Array.isArray(data.defender?.secureScoreDaily)) { 
      const last=[...data.defender.secureScoreDaily].reverse().find(d=>d.percentage!=null); 
      secureScorePrev=pct(last?.percentage); 
    }
    
    // Alert hygiene based on open alerts by severity
    const openAlerts = data.defender?.openAlerts ?? 0;
    const highSeverityAlerts = data.defender?.highSeverityAlerts ?? 0;
    const mediumSeverityAlerts = data.defender?.mediumSeverityAlerts ?? 0;
    let alertHygiene = null;
    if (Number.isFinite(openAlerts)) {
      // Weighted penalty: high = -15pts, medium = -8pts, low = -3pts
      const penalty = (highSeverityAlerts * 15) + (mediumSeverityAlerts * 8) + 
        ((openAlerts - highSeverityAlerts - mediumSeverityAlerts) * 3);
      alertHygiene = Math.max(0, 100 - penalty);
    }
    
    // Threat Hunting score (DLP + Insider Risk incidents)
    const dlpIncidents = data.threatHunting?.totalDLPIncidents ?? 0;
    const insiderRiskIncidents = data.threatHunting?.totalInsiderRiskIncidents ?? 0;
    const totalThreatIncidents = dlpIncidents + insiderRiskIncidents;
    // Logarithmic scoring: 0 = 100, ~10 = 85, ~50 = 74, ~150 = 67
    const threatHuntingScore = totalThreatIncidents === 0 ? 100 : 
      pct(Math.max(0, 100 - Math.log10(totalThreatIncidents + 1) * 15));
    
    // Combine security metrics: Secure Score (40%), Alert Hygiene (30%), Threat Hunting (30%)
    const securityAgg = weighted([
      { value: secureScorePrev, weight: 0.4 }, 
      { value: pct(alertHygiene), weight: 0.3 },
      { value: threatHuntingScore, weight: 0.3 }
    ]);
    
    const securityItems = [
      { label:'Secure Score', value: secureScorePrev }, 
      { label:'Alert Hygiene', value: pct(alertHygiene) },
      { label:'Threat Hunting', value: threatHuntingScore }
    ];
    const securityNotes = [];
    if (openAlerts > 0) securityNotes.push(`${openAlerts} open alerts`);
    if (totalThreatIncidents > 0) securityNotes.push(`${totalThreatIncidents} incidents (30d)`);
    
    cats.push({ 
      key:'security', 
      name:'Security Monitoring', 
      ...securityAgg, 
      items: securityItems,
      notes: securityNotes.join(', ')
    });

    // NEW: Repository Security (GitHub Security - CodeQL, Dependabot, Secret Scanning)
    const ghSec = data.githubSecurity;
    if (ghSec) {
      const codeScanning = ghSec.codeScanning || ghSec.CodeScanning || {};
      const dependabot = ghSec.dependabot || ghSec.Dependabot || {};
      const secretScanning = ghSec.secretScanning || ghSec.SecretScanning || {};
      
      const codeScanOpen = codeScanning.open ?? codeScanning.Open ?? 0;
      const codeScanFixed = codeScanning.fixed ?? codeScanning.Fixed ?? 0;
      const depOpen = dependabot.open ?? dependabot.Open ?? 0;
      const depFixed = dependabot.fixed ?? dependabot.Fixed ?? 0;
      const secretsOpen = secretScanning.open ?? secretScanning.Open ?? 0;
      
      // Get severity breakdown for CodeQL
      const codeBySeverity = codeScanning.bySeverity || codeScanning.BySeverity || {};
      const criticalCode = codeBySeverity.Critical || codeBySeverity.critical || 0;
      const highCode = codeBySeverity.High || codeBySeverity.high || 0;
      
      // Get severity breakdown for Dependabot  
      const depBySeverity = dependabot.bySeverity || dependabot.BySeverity || {};
      const criticalDep = depBySeverity.Critical || depBySeverity.critical || 0;
      const highDep = depBySeverity.High || depBySeverity.high || 0;
      
      // Calculate scores with logarithmic scaling for large alert counts
      const totalCritical = criticalCode + criticalDep;
      const totalHigh = highCode + highDep;
      const totalOpen = codeScanOpen + depOpen;
      const totalFixed = codeScanFixed + depFixed;

      // Vulnerability score: penalize based on severity (logarithmic for high volumes)
      // Reduced penalties for medium/low to reflect manageable risk
      let vulnScore = 100;
      if (totalCritical > 0) vulnScore -= Math.min(40, 15 + Math.log10(totalCritical) * 15);
      if (totalHigh > 0) vulnScore -= Math.min(25, 8 + Math.log10(totalHigh + 1) * 10);
      if (totalOpen - totalCritical - totalHigh > 0) {
        vulnScore -= Math.min(12, Math.log10(totalOpen - totalCritical - totalHigh + 1) * 5);
      }
      vulnScore = pct(Math.max(0, vulnScore));

      // Resolution rate bonus (up to 15 points) — rewards active remediation
      const resolutionRate = (totalFixed + totalOpen) > 0 ? totalFixed / (totalFixed + totalOpen) : 0;
      const resolutionBonus = resolutionRate * 15;
      vulnScore = pct(Math.min(100, (vulnScore || 0) + resolutionBonus));

      // Remediation velocity bonus: reward high volume of fixes (up to 5 points)
      if (totalFixed > 0 && resolutionRate >= 0.5) {
        vulnScore = pct(Math.min(100, (vulnScore || 0) + Math.min(5, Math.log10(totalFixed + 1) * 2.5)));
      }
      
      // Secret scanning (secrets are critical - instant major penalty)
      const secretScore = secretsOpen === 0 ? 100 : pct(Math.max(0, 100 - (secretsOpen * 25)));
      
      const repoSecurityAgg = weighted([
        { value: vulnScore, weight: 0.7 },
        { value: secretScore, weight: 0.3 }
      ]);
      
      const repoSecItems = [
        { label: 'Vulnerability Score', value: vulnScore },
        { label: 'Secret Safety', value: secretScore }
      ];
      const repoSecNotes = [];
      if (totalOpen > 0) repoSecNotes.push(`${totalOpen} open`);
      if (totalFixed > 0) repoSecNotes.push(`${totalFixed} fixed`);
      if (resolutionRate > 0) repoSecNotes.push(`${(resolutionRate * 100).toFixed(0)}% resolved`);
      
      cats.push({
        key: 'repoSecurity',
        name: 'Repository Security',
        ...repoSecurityAgg,
        items: repoSecItems,
        notes: repoSecNotes.join(', ')
      });
    }

    // NEW: Microsoft Purview Compliance
    const purview = data.msPurview;
    if (purview) {
      const complianceHealthScore = purview.healthScore != null ? pct(purview.healthScore) : null;
      const dlpResolutionRate = purview.dlpAlertResolutionRate != null ? pct(purview.dlpAlertResolutionRate) : null;
      const labelCoverage = purview.labelCoverageRate != null ? pct(purview.labelCoverageRate) : null;
      const retentionPolicyRate = purview.retentionPolicyActiveRate != null ? pct(purview.retentionPolicyActiveRate) : null;
      
      const complianceAgg = weighted([
        { value: complianceHealthScore, weight: 0.4 },
        { value: dlpResolutionRate, weight: 0.3 },
        { value: labelCoverage, weight: 0.2 },
        { value: retentionPolicyRate, weight: 0.1 }
      ]);
      
      const complianceItems = [
        { label: 'Compliance Health', value: complianceHealthScore },
        { label: 'DLP Resolution', value: dlpResolutionRate },
        { label: 'Label Coverage', value: labelCoverage },
        { label: 'Retention Policies', value: retentionPolicyRate }
      ];
      const complianceNotes = [];
      if (purview.totalDlpAlerts) complianceNotes.push(`${purview.totalDlpAlerts} DLP alerts`);
      if (purview.criticalDlpAlerts > 0) complianceNotes.push(`${purview.criticalDlpAlerts} critical`);
      if (purview.openDlpAlerts > 0) complianceNotes.push(`${purview.openDlpAlerts} open`);
      
      cats.push({
        key: 'compliance',
        name: 'Compliance & Governance',
        ...complianceAgg,
        items: complianceItems,
        notes: complianceNotes.join(', ')
      });
    }

    // Backup & Recovery
    const rub = data.rubrik || {};
    const jobStatsAvailable = rub.jobStatsAvailable !== false;
    const compliantRate = rub.complianceRate != null ? rub.complianceRate : (()=>{ const c=rub.compliantObjects||rub.successfulTasks; const nc=rub.nonCompliantObjects||rub.failedObjects; if(c==null||nc==null) return null; return (c/(c+nc))*100; })();
    const backupSuccess = (jobStatsAvailable && Number.isFinite(rub.backupJobsTotal) && rub.backupJobsTotal>0) ? pct(rub.backupSuccessRate) : null;
    const restoreSuccess = (jobStatsAvailable && Number.isFinite(rub.restoreJobsTotal) && rub.restoreJobsTotal>0) ? pct(rub.restoreSuccessRate) : null;
    const deltaBackup = Number.isFinite(rub.deltaBackupSuccessRate)? rub.deltaBackupSuccessRate : null;
    const deltaRestore = Number.isFinite(rub.deltaRestoreSuccessRate)? rub.deltaRestoreSuccessRate : null;
    const backupAgg = weighted([
      { value:pct(compliantRate), weight:0.5 },
      { value:backupSuccess, weight:0.3 },
      { value:restoreSuccess, weight:0.2 }
    ]);
    cats.push({ key:'backup', name:'Backup & Recovery', ...backupAgg, items:[
      { label:'Compliance', value:pct(compliantRate) },
      { label:`Backup Success${deltaBackup!=null? (deltaBackup>=0? ' ↑':' ↓'):''}`, value:backupSuccess },
      { label:`Restore Success${deltaRestore!=null? (deltaRestore>=0? ' ↑':' ↓'):''}`, value:restoreSuccess }
    ]});

    // Support & Responsiveness (IT Related Action Items — closure, backlog & SLA)
    const totalTickets = data.tickets?.totalTickets;
    const closedTickets = data.tickets?.closedTickets;
    const openTickets = data.tickets?.openTickets ?? 0;
    const closureRateRaw = (Number.isFinite(data.tickets?.closureRate))
      ? data.tickets.closureRate
      : ((Number.isFinite(totalTickets) && totalTickets > 0 && Number.isFinite(closedTickets))
        ? (closedTickets / totalTickets) * 100 : null);

    // Closure quality: scale so 80% closure = 100, scale linearly below
    const closureQuality = closureRateRaw != null ? pct(Math.min(100, closureRateRaw * 1.25)) : null;

    // Open ticket aging: penalise accumulation of open tickets relative to volume
    let openBacklogScore = null;
    if (Number.isFinite(totalTickets) && totalTickets > 0) {
      const openRatio = openTickets / totalTickets;
      // 0% open = 100, 10% = 90, 30% = 70, 50% = 50, 80%+ = 20
      openBacklogScore = pct(clamp(100 - (openRatio * 100), 20, 100));
    }

    // SLA health: resolution SLA compliance from SlaTracking (null when nothing tracked)
    const slaScore = Number.isFinite(data.tickets?.slaResolutionCompliance)
      ? pct(data.tickets.slaResolutionCompliance) : null;

    const supportAgg = weighted([
      { value: closureQuality, weight: 0.50 },
      { value: openBacklogScore, weight: 0.30 },
      { value: slaScore, weight: 0.20 }
    ]);
    const supportItems = [
      { label: 'Closure Quality', value: closureQuality },
      { label: 'Open Backlog', value: openBacklogScore },
      { label: 'Resolution SLA', value: slaScore }
    ];
    const supportNotes = [];
    if (Number.isFinite(totalTickets)) supportNotes.push(`${totalTickets} total`);
    if (closedTickets > 0) supportNotes.push(`${closedTickets} closed`);
    if (openTickets > 0) supportNotes.push(`${openTickets} open`);
    cats.push({ key: 'support', name: 'Support & Responsiveness', ...supportAgg, items: supportItems, notes: supportNotes.join(', ') });

    // Infrastructure
    const online=data.proxmox?.totals?.onlineNodes; const nodes=data.proxmox?.totals?.nodeCount; const nodesAvail=(Number.isFinite(online)&&Number.isFinite(nodes)&&nodes>0)? (online/nodes)*100:null; const cpu=data.proxmox?.totals?.cpuUsagePercent; const mem=data.proxmox?.totals?.memUsedPercent; const capacity=(Number.isFinite(cpu)&&Number.isFinite(mem))? pct(100 - ((cpu+mem)/2)):null; const infraAgg=weighted([{ value:pct(nodesAvail), weight:0.5 }, { value:capacity, weight:0.5 }]);
    cats.push({ key:'infra', name:'Infrastructure / Virtualization', ...infraAgg, items:[{ label:'Node Availability', value:pct(nodesAvail) }, { label:'Capacity Headroom', value:capacity }]});

    // Network Reliability
    let mallUptime=null;
    let mallEventCount = 0;
    if (data.mall?.events) {
      const events = data.mall.events;
      mallEventCount = events.length;
      const siteIntervals = new Map();
      const sitesSeen = new Set();
      for (const e of events) {
        const ts = e.clock? +e.clock * 1000 : null;
        const durMs = e.duration_seconds? e.duration_seconds * 1000 : 0;
        const hosts = Array.isArray(e.hosts)? e.hosts.map(h=>h.name).filter(Boolean):[];
        hosts.forEach(h=> sitesSeen.add(h));
        if (ts && durMs>0) {
          const start = ts; const end = ts + durMs;
            for (const h of hosts) {
              if (!siteIntervals.has(h)) siteIntervals.set(h, []);
              siteIntervals.get(h).push([start,end]);
            }
        }
      }
      if (sitesSeen.size) {
        const ref = new Date(prevMonthDate.getFullYear(), prevMonthDate.getMonth()+1,0);
        const daysInMonth = ref.getDate();
        const monitoredHours = daysInMonth * 24;
        function mergedHours(intervals) {
          if (!intervals || !intervals.length) return 0;
          const s = intervals.slice().sort((a, b) => a[0] - b[0]);
          let total = 0; let [cs, ce] = s[0];
          for (let i = 1; i < s.length; i++) {
            const [st, en] = s[i];
            if (st <= ce) { ce = Math.max(ce, en); } else { total += ce - cs; cs = st; ce = en; }
          }
          total += ce - cs; return total / 3600000;
        }
        let sumPercent = 0; let countSites = 0;
        for (const site of sitesSeen) {
          const hoursDown = mergedHours(siteIntervals.get(site));
          const sla = monitoredHours>0? ((monitoredHours - hoursDown)/monitoredHours)*100 : null;
          if (sla!=null){ sumPercent += sla; countSites++; }
        }
        if (countSites) mallUptime = pct(sumPercent / countSites);
      }
    }
    // If the InfluxDB query returned successfully with zero events for the month,
    // that is a meaningful answer ("no outages detected") — score it as 100% uptime
    // rather than null. Without this, the Network Reliability category drops to
    // ~40% confidence (only the network-security signal remains) any time the month
    // is outage-free, which contradicts the headline "100% uptime" displayed on the
    // Network Reliability section.
    if (mallUptime == null && Array.isArray(data.mall?.events) && mallEventCount === 0) {
      mallUptime = 100;
    }
    
    // Enhanced Network Reliability with Network Security data
    const netSec = data.networkSecurity;
    let networkSecurityScore = null;
    const networkSecNotes = [];

    if (netSec) {
      const activeAlerts = netSec.totalActiveAlerts ?? 0;
      const blacklistedIPs = netSec.recentBlacklistedIPs ?? 0;
      const failedAuth = netSec.failedAuthAttempts ?? 0;
      // Blacklisted IPs are excluded from threat scoring — they represent successful
      // defensive blocks, not active threats. Only actual unresolved security issues
      // and failed auth attempts (potential brute-force attacks) count as threats.
      // Packet loss events are also excluded (reliability concern, not security).
      const totalNetThreats = activeAlerts + failedAuth;

      // Network threats use logarithmic scaling
      // 0 threats = 100, ~10 = 85, ~100 = 70, ~1000 = 55
      networkSecurityScore = totalNetThreats === 0 ? 100 : 
        pct(Math.max(0, 100 - Math.log10(totalNetThreats + 1) * 15));

      if (totalNetThreats > 0) networkSecNotes.push(`${totalNetThreats} security threats (7d)`);
      if (blacklistedIPs > 0) networkSecNotes.push(`${blacklistedIPs} blocked IPs`);
      if (netSec.highRiskRouters?.length > 0) {
        networkSecNotes.push(`${netSec.highRiskRouters.length} high-risk routers`);
        // Additional penalty for high-risk routers
        networkSecurityScore = pct(Math.max(0, (networkSecurityScore || 0) - (netSec.highRiskRouters.length * 5)));
      }
    }
    
    // Combine uptime (60%) with network security (40%) if both available
    const networkItems = [{ label:'Internet Uptime', value: mallUptime }];
    if (networkSecurityScore != null) {
      networkItems.push({ label:'Network Security', value: networkSecurityScore });
    }
    
    const networkAgg = weighted([
      { value: mallUptime, weight: 0.6 },
      { value: networkSecurityScore, weight: 0.4 }
    ]);
    
    cats.push({ 
      key:'network', 
      name:'Network Reliability', 
      ...networkAgg, 
      items: networkItems,
      notes: networkSecNotes.join(', ')
    });

    // Development & Delivery (revised scoring with MTTR)
    const deploymentsPerWeek=ghCompare?.previous?.dora?.deploymentFrequencyPerWeek;
    const deployScoreRaw = mapDeployFreqScore(deploymentsPerWeek);
    const cfr=cfrScore(ghCompare?.previous?.dora?.changeFailureRate);
    const leadMinutes = parseDurationMinutes(ghCompare?.previous?.dora?.leadTimeP50);
    let leadP50=leadTimeScore(leadMinutes);
    const mttrMinutes = parseDurationMinutes(ghCompare?.previous?.dora?.meanTimeToRestore);
    const mttrScore = mttrScoreFn(mttrMinutes);
    let leadImputed=false, mttrImputed=false;
    if (leadP50 == null && (deployScoreRaw!=null || cfr!=null || mttrScore!=null)) { leadP50 = 50; leadImputed=true; }
    if (mttrScore == null && (deployScoreRaw!=null || cfr!=null || leadP50!=null)) { mttrImputed=true; }

    // New weights: Deploy 25%, Change Failure 25%, Lead Time 30%, MTTR 20%
    let devScore = null; const wDeploy=0.25, wCfr=0.25, wLead=0.30, wMttr=0.20;
    if (deployScoreRaw!=null || cfr!=null || leadP50!=null || mttrScore!=null) {
      const d = deployScoreRaw ?? 50; // neutral if missing
      const f = cfr ?? 50;
      const l = leadP50 ?? 50;
      const m = mttrScore ?? 50;
      devScore = (d*wDeploy)+(f*wCfr)+(l*wLead)+(m*wMttr);
      const coreMissing = deployScoreRaw==null || cfr==null || leadP50==null || mttrScore==null || leadImputed || mttrImputed;
      if (coreMissing && devScore>85) devScore = 85; // cap when incomplete
    }
    let devConfidence = 1;
    // Confidence model: each missing/provisional DORA component has a different
    // weight in our overall scoring (Deploy 25%, CFR 25%, Lead 30%, MTTR 20%) and
    // therefore should subtract proportionally — not a flat 0.2 each. Previously
    // we deducted 0.2 per missing component which dragged confidence to 60% even
    // when Deployment Frequency and Change Failure Rate (the two most reliable
    // DORA signals) were both fully measured. The new model reflects the actual
    // weight contribution of each component and treats provisional values (best-
    // effort imputation) as a smaller hit than a fully missing metric.
    if (deployScoreRaw == null) devConfidence -= 0.25;
    if (cfr == null)            devConfidence -= 0.25;
    if (leadP50 == null)        devConfidence -= 0.30;
    else if (leadImputed)       devConfidence -= 0.10; // provisional ≠ missing
    if (mttrScore == null)      devConfidence -= 0.20;
    else if (mttrImputed)       devConfidence -= 0.07; // provisional ≠ missing
    devConfidence = Math.max(0.3, devConfidence);
    const devItems = [
      { label:'Deployment Freq', value: deployScoreRaw!=null? pct(deployScoreRaw): null },
      { label:'Change Failure', value: cfr },
      { label: leadImputed? 'Lead Time (P50)*' : 'Lead Time (P50)', value: leadP50 },
      { label: mttrImputed? 'MTTR*' : 'MTTR', value: mttrScore }
    ];
    const devNotes = [];
    if (leadImputed) devNotes.push('Lead provisional');
    if (mttrImputed) devNotes.push('MTTR provisional');
    if (deploymentsPerWeek!=null) devNotes.push(`${deploymentsPerWeek.toFixed(2)}/wk`);
    if (mttrMinutes!=null && !mttrImputed) devNotes.push(`MTTR ${Math.round(mttrMinutes)}m`);
    cats.push({ key:'dev', name:'Development & Delivery', score: devScore, confidence: devConfidence, items: devItems, notes: devNotes.join(', ') });

    // Project Management (GitHub Issues Health — velocity & bug responsiveness)
    const ghIssues = data.githubIssuesHealth?.data ?? data.githubIssuesHealth;
    if (ghIssues) {
      const created = ghIssues.createdLastMonth ?? 0;
      const closed = ghIssues.closedLastMonth ?? 0;
      const aging = ghIssues.aging ?? {};
      const bugCount = aging.bugCount ?? 0;
      const bugAging90Plus = aging.bugAging90Plus ?? 0;
      const totalCounted = aging.totalCounted ?? 0;

      // Velocity score
      // ────────────────────────────────────────────────────────────────────
      // Per project guidelines: "Large planned backlogs are expected; only stale
      // bugs penalise." Scoring purely on closed/created ratio is unfair to a
      // small team (1 software engineer + HoD contributor) when leadership is
      // deliberately stocking the backlog with future-phase work — the engineer
      // may be closing 20+ issues/month yet the ratio sits at 0.2 simply because
      // the backlog is being filled faster than any single engineer could drain.
      //
      // We therefore base velocity primarily on ABSOLUTE THROUGHPUT (closures in
      // the period) — calibrated for a 1-2 person delivery team — and only use
      // the closure ratio as a small modifier. This rewards real work done
      // without penalising healthy long-range planning.
      let pmVelocity;
      if (created === 0 && closed === 0) {
        pmVelocity = 70; // genuinely no activity — neutral, low confidence
      } else {
        // Throughput tiers (issues closed by the team in the reporting month).
        // Calibrated for a small team: 10/mo is solid, 20+/mo is strong.
        let throughputScore;
        if      (closed >= 40) throughputScore = 100;
        else if (closed >= 25) throughputScore = 95;
        else if (closed >= 15) throughputScore = 90;
        else if (closed >= 8)  throughputScore = 80;
        else if (closed >= 4)  throughputScore = 70;
        else if (closed >= 1)  throughputScore = 55;
        else                   throughputScore = 30;

        // Closure-ratio modifier (-5 to +10) — encourages keeping pace with new
        // work, but never dominates the score. Capped so a healthy throughput
        // can still score in the 90s even with a deliberately growing backlog.
        let ratioModifier = 0;
        if (created > 0) {
          const cr = closed / created;
          if      (cr >= 1.0) ratioModifier = 10;
          else if (cr >= 0.7) ratioModifier = 5;
          else if (cr >= 0.4) ratioModifier = 0;
          else if (cr >= 0.2) ratioModifier = -3;
          else                ratioModifier = -5;
        } else if (closed > 0) {
          ratioModifier = 5; // closed work without new intake is a positive signal
        }

        pmVelocity = clamp(throughputScore + ratioModifier, 0, 100);
      }

      // Bug responsiveness (stale bugs only)
      let pmBugs = 100;
      if (bugCount > 0) pmBugs = Math.round(100 - (bugAging90Plus / bugCount) * 60);

      // Backlog freshness
      let pmFresh = 70;
      if (totalCounted > 0) {
        const recent = (aging.days0To7 ?? 0) + (aging.days8To30 ?? 0);
        const rr = recent / totalCounted;
        pmFresh = rr >= 0.15 ? 100 : rr >= 0.08 ? 85 : rr >= 0.03 ? 70 : 55;
      }

      const pmScore = pct(pmVelocity * 0.50 + pmBugs * 0.30 + pmFresh * 0.20);
      const closureRatePct = created > 0 ? Math.round((closed / created) * 100) : (closed > 0 ? 100 : null);
      const pmItems = [
        { label: 'Velocity', value: pct(pmVelocity) },
        { label: 'Bug Responsiveness', value: pct(pmBugs) },
        { label: 'Backlog Freshness', value: pct(pmFresh) }
      ];
      const pmNotes = [];
      if (closed > 0) pmNotes.push(`${closed} closed`);
      if (closureRatePct != null) pmNotes.push(`${closureRatePct}% closure rate`);
      if (bugAging90Plus > 0) pmNotes.push(`${bugAging90Plus} stale bugs`);
      cats.push({ key: 'projectMgmt', name: 'Project Management', ...weighted([{ value: pmScore, weight: 1 }]), items: pmItems, notes: pmNotes.join(', ') });
    }

    // Board Flow Health (GitHub Project v2 — WIP staleness, deployment throughput)
    // Per project guidelines: "Large planned backlogs are expected; only stale bugs penalise."
    // Staleness measured relative to active-work items only, not total board (which includes planned roadmap).
    const pf = data.projectFlow?.data ?? data.projectFlow;
    if (pf) {
      const statusSnapshot = pf.statusSnapshot || [];
      const totalBoardItems = statusSnapshot.reduce((s, x) => s + (x.count || 0), 0);
      const staleness = pf.staleness || {};
      const movement = pf.movement || {};

      if (totalBoardItems > 0) {
        // Active work items only (exclude Backlog and Tech Debt — those are planned roadmap items)
        const activeItems = statusSnapshot
          .filter(x => {
            const st = (x.status || '').toLowerCase();
            return st !== 'backlog' && st !== 'tech debt';
          })
          .reduce((s, x) => s + (x.count || 0), 0);
        const activeDenom = Math.max(activeItems, 1);

        // Staleness score: penalise only active-work statuses relative to active items
        let bfStaleness = 100;
        const inProgStale = staleness.inProgressOver14Days || 0;
        const qaStale = staleness.devQaOver14Days || 0;
        const problematicStale = staleness.problematicOver7Days || 0;
        // Tech Debt staleness: informational only — NOT penalised
        if (inProgStale > 0) bfStaleness -= Math.min(25, (inProgStale / activeDenom) * 100);
        if (qaStale > 0) bfStaleness -= Math.min(20, (qaStale / activeDenom) * 80);
        if (problematicStale > 0) bfStaleness -= Math.min(30, (problematicStale / activeDenom) * 150);
        bfStaleness = pct(Math.max(0, bfStaleness));

        // Deployment throughput: reward items moving to done
        const deployed = movement.movedToDeployed ?? 0;
        const created = movement.created ?? 0;
        const closed = movement.closed ?? 0;
        let bfThroughput = 70; // neutral default
        const completions = deployed + closed;
        if (completions > 0 || created > 0) {
          const ratio = created > 0 ? completions / created : (completions > 0 ? 1 : 0);
          bfThroughput = ratio >= 1 ? 100 : ratio >= 0.7 ? 85 : ratio >= 0.4 ? 70 : ratio >= 0.2 ? 55 : 40;
        }
        bfThroughput = pct(bfThroughput);

        const bfAgg = weighted([
          { value: bfStaleness, weight: 0.6 },
          { value: bfThroughput, weight: 0.4 }
        ]);
        const bfItems = [
          { label: 'Flow Health', value: bfStaleness },
          { label: 'Deployment Throughput', value: bfThroughput }
        ];
        const bfNotes = [];
        bfNotes.push(`${totalBoardItems} board items`);
        if (deployed > 0) bfNotes.push(`${deployed} deployed`);
        const totalStale = inProgStale + qaStale + problematicStale;
        if (totalStale > 0) bfNotes.push(`${totalStale} stale`);
        if (problematicStale > 0) bfNotes.push(`${problematicStale} problematic`);

        cats.push({ key: 'boardFlow', name: 'Delivery Flow', ...bfAgg, items: bfItems, notes: bfNotes.join(', ') });
      }
    }

    // Sentry Production Health (error monitoring)
    const sentry = data.sentryHealth?.data ?? data.sentryHealth;
    if (sentry) {
      const unresolved = sentry.totalUnresolvedIssues ?? 0;
      const newIssues = sentry.totalNewIssuesLastMonth ?? 0;
      const eventVolume = sentry.eventVolumeTrend ?? [];

      // Unresolved issues score (logarithmic — tolerant of small counts)
      let unresolvedScore = 100;
      if (unresolved > 0) unresolvedScore = pct(Math.max(0, 100 - Math.min(40, 10 + Math.log10(unresolved + 1) * 15)));

      // New issues score
      let newIssuesScore = 100;
      if (newIssues > 10) newIssuesScore = pct(Math.max(0, 100 - Math.min(25, (newIssues - 10) / 2)));

      // Event stability bonus
      let stabilityScore = 70; // neutral default
      if (eventVolume.length >= 4) {
        const recent = eventVolume.slice(-3).reduce((s, v) => s + (v.events ?? 0), 0);
        const older = eventVolume.slice(0, 3).reduce((s, v) => s + (v.events ?? 0), 0);
        if (older > 0 && recent < older) stabilityScore = pct(Math.min(100, 70 + ((older - recent) / older) * 30));
        else if (recent > older && older > 0) stabilityScore = pct(Math.max(40, 70 - ((recent - older) / older) * 30));
      }

      const sentryAgg = weighted([
        { value: unresolvedScore, weight: 0.5 },
        { value: newIssuesScore, weight: 0.3 },
        { value: stabilityScore, weight: 0.2 }
      ]);
      const sentryItems = [
        { label: 'Unresolved Issues', value: unresolvedScore },
        { label: 'New Issues', value: newIssuesScore },
        { label: 'Event Stability', value: stabilityScore }
      ];
      const sentryNotes = [];
      if (unresolved > 0) sentryNotes.push(`${unresolved} unresolved`);
      if (newIssues > 0) sentryNotes.push(`${newIssues} new last month`);
      cats.push({ key: 'sentry', name: 'Production Stability', ...sentryAgg, items: sentryItems, notes: sentryNotes.join(', ') });
    }

    // Email (enhanced with volume trend and internal/external balance)
    const prevMail = data.mailCompare?.previous;
    const priorMail = data.mailCompare?.prior;
    const readRate = (()=>{
      if (!prevMail || !prevMail.totalReceived) return null;
      return pct((prevMail.readCount / prevMail.totalReceived) * 100);
    })();

    // Volume stability: compare month-over-month total volume change
    // Massive spikes or drops may indicate delivery issues or spam waves
    let volumeStabilityScore = null;
    if (prevMail && priorMail) {
      const prevTotal = (prevMail.totalSent || 0) + (prevMail.totalReceived || 0);
      const priorTotal = (priorMail.totalSent || 0) + (priorMail.totalReceived || 0);
      if (priorTotal > 0) {
        const changeRatio = Math.abs(prevTotal - priorTotal) / priorTotal;
        // 0-10% change = 100, 10-30% = 85, 30-50% = 70, 50-100% = 55, >100% = 40
        volumeStabilityScore = changeRatio <= 0.1 ? 100 : changeRatio <= 0.3 ? 85 :
          changeRatio <= 0.5 ? 70 : changeRatio <= 1.0 ? 55 : 40;
        volumeStabilityScore = pct(volumeStabilityScore);
      }
    }

    // External communication ratio: healthy orgs have balanced internal/external
    let externalBalanceScore = null;
    if (prevMail && prevMail.totalSent > 0) {
      const extRatio = (prevMail.sentExternal || 0) / prevMail.totalSent;
      // 20-60% external is healthy, extremes are less healthy
      if (extRatio >= 0.2 && extRatio <= 0.6) externalBalanceScore = 100;
      else if (extRatio >= 0.1 && extRatio <= 0.7) externalBalanceScore = 85;
      else externalBalanceScore = 70;
      externalBalanceScore = pct(externalBalanceScore);
    }

    const emailAgg = weighted([
      { value: readRate, weight: 0.50 },
      { value: volumeStabilityScore, weight: 0.30 },
      { value: externalBalanceScore, weight: 0.20 }
    ]);
    const emailItems = [
      { label: 'Read Rate', value: readRate },
      { label: 'Volume Stability', value: volumeStabilityScore },
      { label: 'Ext. Balance', value: externalBalanceScore }
    ];
    const emailNotes = [];
    if (prevMail) {
      emailNotes.push(`${(prevMail.totalReceived || 0).toLocaleString()} received`);
      emailNotes.push(`${(prevMail.totalSent || 0).toLocaleString()} sent`);
    }
    cats.push({ key: 'email', name: 'Email Operations', ...emailAgg, items: emailItems, notes: emailNotes.join(', ') });

    // SharePoint Collaboration (improved with ALL sites data)
    const spHealth = data.sharepointHealth || {};
    let activePrev = pct(spHealth.activePrevMonthPct ?? spHealth.ActivePrevMonthPct);
    let headroom = pct(spHealth.capacityHeadroomPct ?? spHealth.CapacityHeadroomPct);
    const growthRaw = spHealth.storageGrowthPct ?? spHealth.StorageGrowthPct;
    const growthPct = Number.isFinite(growthRaw) ? growthRaw : null;
    const limitedData = (spHealth.limitedDataCoverage ?? spHealth.LimitedDataCoverage) === true;
    const activeLast30 = pct(spHealth.activeLast30DaysPct ?? spHealth.ActiveLast30DaysPct);
    const totalSiteCount = spHealth.totalSiteCount ?? spHealth.TotalSiteCount ?? 0;
    let activePrevIsFallback = false;

    // Fallback: if previous full month shows 0% (or null) but we have recent activity (last 30 days) AND data window is limited
    // (typical immediately after migration) use the last 30 days % as a proxy so score is not unfairly anchored at 0.
    if ((activePrev == null || activePrev === 0) && activeLast30 != null && activeLast30 > 0 && limitedData) {
      activePrev = activeLast30;
      activePrevIsFallback = true;
    }

    const { score: collabScore, confidence: collabConfidenceBase, growthProfileScore } = computeSharepointCollabAggregate(activePrev, headroom, growthPct);
    const collabConfidence = activePrevIsFallback ? collabConfidenceBase * 0.85 : collabConfidenceBase; // slight penalty for proxy metric
    
    const spItems = [
      { label: activePrevIsFallback? 'Active (30d proxy)' : 'Active Sites', value: activePrev },
      { label:'Capacity Remaining', value: headroom },
      ...(growthProfileScore!=null? [{ label:'Growth Health', value: pct(growthProfileScore) }]:[])
    ];
    
    // Add context for directors
    const spNotes = [];
    if (totalSiteCount > 0) spNotes.push(`${totalSiteCount} sites monitored`);
    if (limitedData) spNotes.push('Limited data coverage');
    if (activePrevIsFallback) spNotes.push('Using recent 30-day activity');
    
    cats.push({
      key:'sharepoint', 
      name:'SharePoint Collaboration', 
      score: collabScore, 
      confidence: collabConfidence, 
      items: spItems,
      notes: spNotes.join(', ')
    });

    // Telephony & Voice (removed stability metric – only Cost Efficiency considered)
    const daily = Array.isArray(data.portaDaily)? data.portaDaily: [];
    const totalCalls = daily.reduce((a,r)=> a + (r.incoming||0) + (r.outgoing||0),0);
    const highCostCount = data.portaTops?.highCostCalls? data.portaTops.highCostCalls.length : 0;
    const highCostRatio = (totalCalls>0)? (highCostCount/totalCalls): null;
    const costEfficiency = highCostRatio!=null? pct((1 - Math.min(1,highCostRatio))*100): null;
    const telAgg = weighted([{ value:costEfficiency, weight:1 }]);
    cats.push({ key:'telephony', name:'Telephony & Voice', ...telAgg, items:[{ label:'Cost Efficiency', value:costEfficiency }]});

    // Tenant Statements
    const ts = Array.isArray(data.tenantStatements)? data.tenantStatements: [];
    const tsRec = (()=>{ const targetYear=prevMonthDate.getFullYear(); const targetMonth=prevMonthDate.getMonth()+1; return ts.find(r=> (r.year===targetYear && r.month===targetMonth)); })();
    let tsSuccess=null; if(tsRec && tsRec.sent>0){ tsSuccess = pct(((tsRec.sent - tsRec.errors)/tsRec.sent)*100); }
    cats.push({ key:'tenant', name:'Tenant Statements Automation', ...weighted([{ value:tsSuccess, weight:1 }]), items:[{ label:'Success Rate', value:tsSuccess }]});

    // Automation & Self-Healing (monitoring coverage + auto-remediation impact)
    // Mirrors AutomationSelfHealingSummary so the headline score matches the report section:
    //   Remediation Success (45%) + Self-Healing (35%) + Coverage (20%).
    // Each signal is null when there is no underlying activity, so weighted() re-normalises
    // and lowers confidence instead of dragging the score to zero — a genuinely quiet month
    // for a well-configured platform is a good thing, not a penalty.
    const auto = data.automation;
    if (auto) {
      const activeRules = (auto.activeSystemRules ?? 0) + (auto.activeEndpointRules ?? 0);
      const autoRules = auto.autoRemediationEnabledRules ?? 0;
      const alertsRaised = auto.alertsRaised ?? 0;
      const alertsAutoResolved = auto.alertsAutoResolved ?? 0;
      const realExecutions = (auto.remediationsExecuted ?? 0) - (auto.remediationsSimulated ?? 0);

      const coverageScore = activeRules > 0 ? pct(Math.min(100, (autoRules / activeRules) * 100)) : null;
      const selfHealScore = alertsRaised > 0 ? pct((alertsAutoResolved / alertsRaised) * 100) : null;
      const successScore = realExecutions > 0 ? pct(auto.remediationSuccessRate) : null;

      const autoAgg = weighted([
        { value: successScore, weight: 0.45 },
        { value: selfHealScore, weight: 0.35 },
        { value: coverageScore, weight: 0.20 }
      ]);

      // If nothing is configured or has happened at all, fall back to a low-confidence neutral
      // so an unconfigured platform neither inflates nor collapses the headline score.
      let autoScore = autoAgg.score;
      let autoConfidence = autoAgg.confidence;
      if (autoScore == null) { autoScore = 70; autoConfidence = 0.3; }

      const autoItems = [
        { label: 'Remediation Success', value: successScore },
        { label: 'Self-Healing', value: selfHealScore },
        { label: 'Coverage', value: coverageScore }
      ];
      const autoNotes = [];
      if (activeRules > 0) autoNotes.push(`${activeRules} active rules (${autoRules} auto)`);
      if (alertsRaised > 0) autoNotes.push(`${alertsAutoResolved}/${alertsRaised} self-healed`);
      if ((auto.estimatedManualHoursSaved ?? 0) > 0) autoNotes.push(`~${auto.estimatedManualHoursSaved}h saved`);
      cats.push({ key: 'automation', name: 'Automation & Self-Healing', score: autoScore, confidence: autoConfidence, items: autoItems, notes: autoNotes.join(', ') });
    }

    // Router Fleet (MikroTik fleet management — mirrors RouterFleetSummary.healthScore)
    //   Online % (50%) + Provisioned % (30%) + No-Offline (20%).
    const fleet = data.routerFleet?.data ?? data.routerFleet;
    if (fleet && fleet.totalRouters) {
      const totalRouters = fleet.totalRouters;
      const monitored = Math.max(1, totalRouters - (fleet.unmonitored ?? 0));
      const onlinePct = pct(((fleet.online ?? 0) / monitored) * 100);
      const provisionedPct = Array.isArray(fleet.provisioningDistribution)
        ? pct(((fleet.provisioningDistribution.find(p => p.status === 'Provisioned')?.count || 0) / totalRouters) * 100)
        : null;
      const offlineRouters = fleet.offline ?? 0;
      const noOffline = pct(offlineRouters === 0 ? 100 : Math.max(0, 100 - (offlineRouters / totalRouters) * 200));

      const fleetAgg = weighted([
        { value: onlinePct, weight: 0.5 },
        { value: provisionedPct, weight: 0.3 },
        { value: noOffline, weight: 0.2 }
      ]);
      const fleetItems = [
        { label: 'Online', value: onlinePct },
        { label: 'Provisioned', value: provisionedPct },
        { label: 'No Offline', value: noOffline }
      ];
      const fleetNotes = [];
      fleetNotes.push(`${totalRouters} routers`);
      fleetNotes.push(`${fleet.online ?? 0} online`);
      if (offlineRouters > 0) fleetNotes.push(`${offlineRouters} offline`);
      cats.push({ key: 'routerFleet', name: 'Router Fleet', ...fleetAgg, items: fleetItems, notes: fleetNotes.join(', ') });
    }

    // Microsoft 365 Configuration Posture (mirrors Microsoft365Compliance findings)
    // Derives a numeric score from the count & severity of live configuration gaps across
    // Purview, Intune, Defender and the Endpoint Health monitor. Score = 100 − Σ(severity
    // penalties), so a clean tenant scores 100 and each gap visibly reduces the score.
    {
      const m365Sources = [data.msPurview, data.intune, data.defender, data.endpointStats];
      const servicesReporting = m365Sources.filter(s => s != null).length;
      if (servicesReporting > 0) {
        let high = 0, medium = 0, low = 0;
        const numOf = (v) => (typeof v === 'number' ? v : 0);

        // ── Microsoft Purview ──
        const pv = data.msPurview;
        if (pv) {
          // One-sided signal - "false" doesn't confirm the tenant lacks DLP policies (see
          // MsPurviewSummary.jsx / Microsoft365Compliance.jsx doc comments), so it's treated as a
          // low-severity "verify" item rather than a confirmed high-severity gap.
          if (pv.hasActiveDlpPolicies === false) low++;
          if (pv.hasSensitivityLabels === false) high++;
          else if (numOf(pv.labelsWithEncryption) === 0 && numOf(pv.enabledSensitivityLabels) > 0) medium++;
          if (pv.hasRetentionPolicies === false) medium++;
          else if (pv.retentionDataAvailable === false) low++;
          if (numOf(pv.labelCoverageRate) > 0 && numOf(pv.labelCoverageRate) < 50) medium++;
          if (numOf(pv.openDlpAlerts) > 0 && numOf(pv.dlpAlertResolutionRate) < 50) medium++;
        }

        // ── Intune (device compliance configuration) ──
        const iv = data.intune;
        if (iv) {
          const totalDev = iv.totalDevices ?? iv.TotalDevices ?? 0;
          const states = iv.complianceStates ?? iv.ComplianceStates ?? {};
          const compliantDev = states.Compliant ?? states.compliant ?? 0;
          const compRate = iv.previousMonthComplianceRate ?? iv.PreviousMonthComplianceRate ??
            (totalDev > 0 ? (compliantDev / totalDev) * 100 : 0);
          if (totalDev === 0) high++;
          else if (compRate < 60) high++;
          else if (compRate < 80) medium++;
          const unknownDev = states.Unknown ?? states.unknown ?? 0;
          if (unknownDev > 0) low++;
        }

        // ── Defender ──
        const dv = data.defender;
        if (dv) {
          if (numOf(dv.highSeverityAlerts) > 0) high++;
          else if (numOf(dv.openAlerts) > 0) medium++;
        }

        // ── Endpoint Health Monitor ──
        const ep = data.endpointStats;
        if (ep) {
          const activeEp = numOf(ep.totalActive);
          if (activeEp > 10) high++;
          else if (activeEp > 0) medium++;
        }

        const m365Score = pct(Math.max(0, 100 - (high * 15 + medium * 7 + low * 3)));
        // Confidence scales with how many of the four services actually reported.
        const m365Confidence = servicesReporting / 4;
        const m365Items = [
          { label: 'High Gaps', value: high === 0 ? 100 : pct(Math.max(0, 100 - high * 25)) },
          { label: 'Medium Gaps', value: medium === 0 ? 100 : pct(Math.max(0, 100 - medium * 12)) },
          { label: 'Low Gaps', value: low === 0 ? 100 : pct(Math.max(0, 100 - low * 5)) }
        ];
        const m365Notes = [];
        if (high) m365Notes.push(`${high} high`);
        if (medium) m365Notes.push(`${medium} medium`);
        if (low) m365Notes.push(`${low} low`);
        if (!high && !medium && !low) m365Notes.push('No configuration gaps');
        if (servicesReporting < 4) m365Notes.push(`${4 - servicesReporting} service(s) not reporting`);
        cats.push({ key: 'm365compliance', name: 'Microsoft 365 Configuration', score: m365Score, confidence: m365Confidence, items: m365Items, notes: m365Notes.join(', ') });
      }
    }

    return cats;
  }, [data, prevMonthDate]);

  const overallScore = useMemo(()=>{ const present = categoryScores.filter(c=>c.score!=null); const totalW = present.reduce((a,c)=> a + (WEIGHTS[c.key]||0),0); if(!totalW) return null; return present.reduce((a,c)=> a + c.score * ((WEIGHTS[c.key]||0)/totalW),0); }, [categoryScores]);
  const overallConfidence = useMemo(()=>{ let used=0, total=0; categoryScores.forEach(c=>{ const w=WEIGHTS[c.key]||0; total+=w; if(c.score!=null) used+=w * (c.confidence||1); }); if(!total) return 0; return (used/total)*100; }, [categoryScores]);

  const [priorMonthScore,setPriorMonthScore] = useState(()=>{ try { const v=localStorage.getItem(priorMonthKey); return v? +v : null; } catch (catchError) { Sentry.captureException(catchError); return null; }});
  const [priorCategoryScores, setPriorCategoryScores] = useState(()=>{ try { const v=localStorage.getItem(priorMonthKey+'-cats'); return v? JSON.parse(v) : null; } catch (catchError) { Sentry.captureException(catchError); return null; }});
  useEffect(()=>{
    if (overallScore!=null) {
      try {
        localStorage.setItem(monthKey, overallScore.toFixed(2));
        // Store per-category scores for month-over-month comparison
        const catMap = {};
        categoryScores.forEach(c => { if (c.score != null) catMap[c.key] = +c.score.toFixed(2); });
        localStorage.setItem(monthKey+'-cats', JSON.stringify(catMap));
      }
      catch (catchError) {
          Sentry.captureException(catchError);
          /* localStorage not available */
      }
    }
  }, [overallScore, monthKey, categoryScores]);
  useEffect(()=>{
    // Syncs the prior-month score/category snapshot from localStorage (an external
    // store) into React state when the reporting window changes. This is a legitimate
    // external-store → state sync, so the set-state-in-effect rule is suppressed here.
    try {
      const v=localStorage.getItem(priorMonthKey);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      if(v) setPriorMonthScore(+v);
      const cats=localStorage.getItem(priorMonthKey+'-cats');
      if(cats) setPriorCategoryScores(JSON.parse(cats));
    }
    catch (catchError) {
        Sentry.captureException(catchError);
        /* localStorage not available */
    }
  }, [priorMonthKey]);

  if (loading) return <HexagonLoader/>;
  if (error) return <div style={{ color:'red' }}>{error}</div>;
  if (overallScore == null) return null;

  const delta = priorMonthScore!=null ? (overallScore - priorMonthScore) : null;
  const hasPriorCatData = priorCategoryScores != null && Object.keys(priorCategoryScores).length > 0;
  const colorBand = (s)=> { if (s >= 90) return '#2e7d32'; if (s >= 80) return '#388e3c'; if (s >= 70) return '#fbc02d'; if (s >= 60) return '#ffa000'; return '#d32f2f'; };
  const gaugeAngle = scoreToAngle(overallScore);
  const llmProxyText = [
    `Overall IT Service Health Score: ${overallScore.toFixed(1)}% (confidence ${overallConfidence.toFixed(0)}%)`,
    delta != null ? `Month-over-month change: ${delta >= 0 ? '+' : ''}${delta.toFixed(1)} pts` : null,
    '',
    ...categoryScores.map(c => {
      const weight = WEIGHTS[c.key] || 0;
      const scoreStr = c.score != null ? `${c.score.toFixed(1)}%` : 'n/a';
      const confStr = `conf ${((c.confidence || 0) * 100).toFixed(0)}%`;
      const metricsStr = c.items.filter(i => i.value != null).map(i => `${i.label}: ${i.value.toFixed(1)}%`).join(', ');
      const notesStr = c.notes ? ` | ${c.notes}` : '';
      const priorCatScore = priorCategoryScores?.[c.key];
      const catDelta = (c.score != null && priorCatScore != null) ? (c.score - priorCatScore) : null;
      const deltaStr = catDelta != null ? ` (${catDelta >= 0 ? '+' : ''}${catDelta.toFixed(1)} vs prior)` : '';
      return `${c.name} (${(weight * 100).toFixed(0)}% weight): ${scoreStr}${deltaStr} [${confStr}] — ${metricsStr}${notesStr}`;
    })
  ].filter(Boolean).join('\n');

  return (
    <div className="dashboard-card group-wrapper overall-health-score" style={{ position:'relative' }}>
      <div data-llm-proxy style={{ display:'none' }}>{llmProxyText}</div>
      <h2 style={{ marginTop:0 }}>Overall IT Service Health</h2>
      {/* Added class for layout to allow print stacking */}
      <div className="overall-health-flex">
        {/* Gauge */}
        <div className="overall-health-gauge" aria-label="Overall IT Service Health Gauge" role="img" aria-valuenow={overallScore.toFixed(1)} aria-valuemin={0} aria-valuemax={100}>
          <div style={{ width:220, height:140, position:'relative' }}>
            <svg viewBox="0 0 200 120" width="220" height="140">
              {/* Color bands (background arc) */}
              <defs>
                <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#d32f2f" />
                  <stop offset="40%" stopColor="#ffa000" />
                  <stop offset="55%" stopColor="#fbc02d" />
                  <stop offset="75%" stopColor="#388e3c" />
                  <stop offset="100%" stopColor="#2e7d32" />
                </linearGradient>
              </defs>
              <path d="M10 110 A90 90 0 0 1 190 110" fill="none" stroke="url(#gaugeGrad)" strokeWidth="18" strokeLinecap="round" />
              {/* Ticks */}
              {Array.from({ length:11 }, (_,i)=>{ const a=(-110 + i*22) * Math.PI/180; const cx=100+Math.cos(a)*70; const cy=110+Math.sin(a)*70; const x2=100+Math.cos(a)*82; const y2=110+Math.sin(a)*82; return <line key={i} x1={cx} y1={cy} x2={x2} y2={y2} stroke="#444" strokeWidth={i%2===0?2:1} />; })}
              {/* Needle */}
              <g style={{ transition:'transform 0.8s ease-out', transform:`rotate(${gaugeAngle}deg)`, transformOrigin:'100px 110px' }}>
                <line x1="100" y1="110" x2="100" y2="22" stroke="#222" strokeWidth="4" strokeLinecap="round" />
                <circle cx="100" cy="110" r="8" fill="#666" stroke="#222" />
              </g>
            </svg>
          </div>
          <div style={{ fontSize:48, fontWeight:700, lineHeight:1, color:colorBand(overallScore) }}>{overallScore.toFixed(0)}%</div>
          {delta!=null && <div style={{ fontSize:12, color: delta>=0? 'green':'red' }}>{delta>=0?'↑':'↓'} {Math.abs(delta).toFixed(1)} pts vs prior month</div>}
          <div style={{ fontSize:10, marginTop:4, color:'#666' }}>Data confidence: {overallConfidence.toFixed(0)}%</div>
        </div>
        {/* Breakdown table */}
        <div className="overall-health-table">
          <table className="data-table">
            <thead>
              <tr>
                <th className="col-category" style={{ textAlign:'left' }}>Category</th>
                <th className="col-score">Score</th>
                {hasPriorCatData && <th className="col-delta" style={{ textAlign:'center' }}>Δ</th>}
                <th className="col-metrics" style={{ textAlign:'left' }}>Key Metrics</th>
                <th className="col-conf" style={{ textAlign:'right' }}>Conf.</th>
              </tr>
            </thead>
            <tbody>
              {categoryScores.map(c => { 
                const bg = c.score!=null? `${colorBand(c.score)}15` : undefined; 
                const metricsDisplay = c.items.filter(i=>i.value!=null).map(i=> `${i.label} ${i.value.toFixed(0)}%`).join(' • ');
                const fullDisplay = c.notes ? `${metricsDisplay} (${c.notes})` : metricsDisplay;
                const priorCatScore = priorCategoryScores?.[c.key];
                const catDelta = (c.score != null && priorCatScore != null) ? (c.score - priorCatScore) : null;
                // Per-category methodology tooltip — surfaces the formula behind each
                // score on hover so directors reading the report understand why a
                // category is high or low without leaving the page.
                const methodology = {
                  network:        'Network Reliability = Internet Uptime (60%) + Network Security (40%). Uptime is computed from per-minute ICMP availability across all managed routers, with consecutive 5-minute down-windows merged into single incidents. Outages shorter than 60s are filtered as monitoring noise.',
                  dev:            'Dev & Delivery = Deployment Frequency (25%) + Change Failure Rate (25%) + Lead Time P50 (30%) + MTTR (20%). Missing metrics cap the score and reduce confidence proportionally to their weight, so a partial result is never inflated.',
                  pm:             'Project Management = Velocity (50%) + Bug Responsiveness (30%) + Backlog Freshness (20%). Velocity is calibrated for a small delivery team and weighted toward absolute throughput (issues closed in the period); a deliberately growing planned backlog does not penalise the score. Only stale (open >30d) bugs reduce responsiveness.',
                  security:       'Security Monitoring = Secure Score (40%) + Alert Hygiene (30%) + Threat Hunting (30%). Alert hygiene is severity-weighted; threat hunting uses logarithmic scaling so a handful of incidents does not collapse the score.',
                  ghsec:          'Repo Security = Vulnerability Score (70%) + Secret Safety (30%). Vulnerabilities are severity-weighted with logarithmic scaling; resolution rate contributes up to 15 bonus points and remediation velocity up to 5 more.',
                  prodstability:  'Production Stability = Unresolved Issues (50%) + New Issues (30%) + Event Stability (20%). Logarithmic scaling tolerates small numbers of low-severity errors so a single incident does not dominate the score.',
                  backup:         'Backup score reflects Rubrik object compliance: protected vs non-compliant objects, weighted by recent successful task ratio.',
                  intune:         'Device Management score = Device Compliance %. Counts of compliant vs non-compliant Intune devices form the headline ratio.',
                  email:          'Email score blends successful send/receive ratios with mailbox quota health from Exchange Online analytics.',
                  sharepoint:     'SharePoint = Active Sites (50%) + Capacity Remaining (30%) + Growth Health (20%).',
                  asset:          'Device Management score = Device Compliance %. Counts of compliant vs non-compliant Intune devices form the headline ratio.',
                  support:        'Support & Responsiveness = Closure Quality (50%) + Open Backlog (30%) + Resolution SLA (20%). Derived from IT Related Action Items on the configured IT Support board, with SLA compliance from SLA tracking.',
                  infra:          'Infrastructure score reflects Proxmox cluster health: node availability, resource utilisation and replication state.',
                  telephony:      'Telephony score reflects Porta call completion ratio and call quality metrics for the period.',
                  tenantStatements: 'Tenant Statements score reflects on-time generation and delivery success for the period.',
                  automation:     'Automation & Self-Healing = Remediation Success (45%) + Self-Healing (35%) + Coverage (20%). Success is the share of real (non-simulated) remediation runs that completed; self-healing is the share of alerts that auto-recovered without a ticket; coverage is the proportion of active monitoring rules wired for auto-remediation. A quiet, well-configured month is rewarded, not penalised — missing signals lower confidence rather than the score.',
                  routerFleet:    'Router Fleet = Online % (50%) + Provisioned % (30%) + No-Offline (20%). Computed from the MikroTik fleet summary over the last 30 days; offline routers carry a doubled penalty so any hard outage is clearly visible.',
                  m365compliance: 'Microsoft 365 Configuration = 100 − Σ(gap penalties), where each live configuration gap across Purview, Intune, Defender and the Endpoint Health monitor deducts points by severity (high −15, medium −7, low −3). A clean tenant scores 100; confidence scales with how many of the four services are reporting.'
                };
                const methodologyText = methodology[c.key] || '';
                const tooltipText = c.items.filter(i=>i.value!=null).map(i=>`${i.label}: ${i.value.toFixed(1)}%`).join('\n')
                  + (c.notes ? `\n\n${c.notes}` : '')
                  + (methodologyText ? `\n\n— How this is scored —\n${methodologyText}` : '');
                return (
                  <tr key={c.key} style={{ background:bg }} title={tooltipText}>
                    <td className="col-category" style={{ textAlign:'left', fontWeight:600 }}>{c.name}</td>
                    <td className="col-score">{c.score!=null? c.score.toFixed(1)+'%':'n/a'}</td>
                    {hasPriorCatData && (
                      <td className="col-delta" style={{ textAlign:'center', fontSize: 11, fontWeight: 600, color: catDelta != null ? (catDelta >= 0 ? '#2e7d32' : '#d32f2f') : '#999' }}>
                        {catDelta != null ? `${catDelta >= 0 ? '↑' : '↓'}${Math.abs(catDelta).toFixed(1)}` : '—'}
                      </td>
                    )}
                    <td className="col-metrics" style={{ textAlign:'left', fontSize: 11 }}>{fullDisplay}</td>
                    <td className="col-conf" style={{ textAlign:'right' }}>{(c.confidence*100).toFixed(0)}%</td>
                  </tr>
                ); 
              })}
            </tbody>
          </table>
          <div className="chart-subtitle" style={{ marginTop:4, lineHeight:1.4 }}>
            <strong>Scoring weights (sum 100%):</strong> Security(11%) Infrastructure(8%) Backup(8%) Support(8%) Compliance(8%) Network(7%) Development(7%) Asset(6%) Automation &amp; Self-Healing(5%) Repo Security(5%) Email(4%) SharePoint(4%) Project Mgmt(4%) Sentry(4%) M365 Configuration(3%) Router Fleet(3%) Board Flow(3%) Telephony(1%) Tenant Statements(1%). Every IT Report section contributes; categories with no data for the period are omitted and the remaining weights are re-normalised.
            <br/><strong>Security Monitoring:</strong> Secure Score(40%) + Alert Hygiene(30%) + Threat Hunting(30%). Alert hygiene penalizes by severity (high:-15, med:-8, low:-3). Threat hunting uses logarithmic scaling for incident counts.
            <br/><strong>CapTrac Security:</strong> Vulnerability Score(70%) + Secret Safety(30%). Vulnerabilities scored by severity with logarithmic scaling; resolution rate adds up to 15 bonus points plus up to 5 remediation velocity bonus.
            <br/><strong>Network:</strong> Internet Uptime(60%) + Network Security(40%). Network security uses log scaling for active alerts and failed auth attempts. Blacklisted IPs are excluded from threat scoring (they represent successful defensive blocks). Packet loss is excluded (reliability concern, not security).
            <br/><strong>Dev & Delivery:</strong> Deployment Frequency(25%) + Change Failure Rate(25%) + Lead Time P50(30%) + MTTR(20%). Deployment tiers: Daily+ ≥7/wk≈95–100, Weekly≈80, Monthly≈60. Missing metrics cap score & reduce confidence.
            <br/><strong>Project Mgmt:</strong> Velocity(50%) + Bug Responsiveness(30%) + Backlog Freshness(20%). Velocity is based primarily on absolute throughput (issues closed in the period) and calibrated for a small delivery team — closure ratio is only a small modifier so a deliberately growing planned backlog does not depress the score. Only stale bugs penalise.
            <br/><strong>Production Stability:</strong> Unresolved Issues(50%) + New Issues(30%) + Event Stability(20%). Logarithmic scaling tolerates small error counts.
            <br/><strong>SharePoint:</strong> Active Sites(50%) + Capacity Remaining(30%) + Growth Health(20%).
            <br/><strong>Automation & Self-Healing:</strong> Remediation Success(45%) + Self-Healing(35%) + Coverage(20%). Measures how much manual IT effort the monitoring + auto-remediation engines absorb; a quiet, well-configured month is rewarded and missing signals lower confidence rather than the score.
            <br/><strong>Router Fleet:</strong> Online %(50%) + Provisioned %(30%) + No-Offline(20%) from the MikroTik fleet summary (offline routers carry a doubled penalty).
            <br/><strong>M365 Configuration:</strong> 100 − Σ(gap penalties) across Purview, Intune, Defender and the Endpoint Health monitor (high −15, medium −7, low −3); confidence scales with the number of services reporting.
            <br/>Missing metrics reduce overall confidence but don't artificially inflate scores. * indicates provisional neutral values.
            <br/><strong>Team-size context:</strong> Dev &amp; Delivery and Project Mgmt scores are calibrated for a small in-house team. Industry-standard DORA tiers (which assume large engineering organisations) are not applied directly — instead, throughput tiers and closure ratios are used so the scores fairly reflect output relative to capacity.
          </div>
        </div>
      </div>
      {/* AI Health Score Commentary */}
      <HealthScoreCommentary healthScoreData={llmProxyText} />
    </div>
  );
}
